/*
  <otw-countdown> (PRD §9)

  <otw-countdown data-end="2026-10-04T23:59:59+05:30"></otw-countdown>
  <otw-countdown data-daily="true" [data-reset="00:00"]></otw-countdown>

  - data-daily="true": counts down to the next daily reset in Asia/Kolkata (default midnight),
    read with Intl.DateTimeFormat, then starts again.
  - data-end: ISO date. Offsets like +0530 are accepted. When it passes, the element gets
    .is-expired and fires an `expired` event (bubbles). Daily timers fire `expired` at each reset.
  - Ticks once per second with requestAnimationFrame; values are zero-padded and get an
    .is-tick class (fade/flip) when they change.
  - Renders its own markup unless it already contains [data-unit="days|hours|minutes|seconds"].
  - Unit labels come from data-label-days/hours/minutes/seconds or OTW.strings.
*/
(() => {
  if (customElements.get('otw-countdown')) return;

  const OTW = (window.OTW = window.OTW || {});
  const t = (key, fallback) => (OTW.strings && OTW.strings[key]) || fallback;
  const TZ = 'Asia/Kolkata';
  const DAY = 86400;

  let kolkataFormat;
  function kolkataSecondsOfDay(date = new Date()) {
    kolkataFormat ||= new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const parts = {};
    kolkataFormat.formatToParts(date).forEach((p) => (parts[p.type] = p.value));
    return (Number(parts.hour) % 24) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
  }

  function parseISO(value) {
    if (!value) return NaN;
    if (/^\d+$/.test(value)) return Number(value) * 1000; // unix seconds
    // Safari needs "+05:30", not "+0530"; also allow a space instead of "T"
    const normalised = value.trim().replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    return Date.parse(normalised);
  }

  const pad = (n) => String(n).padStart(2, '0');

  class OtwCountdown extends HTMLElement {
    connectedCallback() {
      this.daily = this.dataset.daily === 'true' || this.dataset.daily === '';
      this.end = parseISO(this.dataset.end);
      const [h = 0, m = 0] = (this.dataset.reset || '00:00').split(':').map(Number);
      this.resetAt = (h * 3600 + m * 60) % DAY;
      this.render();
      this.last = null;
      this.expired = false;
      this.tick = this.tick.bind(this);
      this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
    }

    render() {
      this.units = {};
      const existing = this.querySelectorAll('[data-unit]');
      if (existing.length) {
        existing.forEach((el) => (this.units[el.dataset.unit] = el));
        return;
      }
      const label = (unit, fallback) => this.dataset[`label${unit[0].toUpperCase()}${unit.slice(1)}`] || t(unit, fallback);
      const units = [
        ['days', 'd'],
        ['hours', 'h'],
        ['minutes', 'm'],
        ['seconds', 's'],
      ];
      this.setAttribute('role', 'timer');
      this.innerHTML = units
        .map(
          ([unit, fallback], i) =>
            `${i ? '<span class="otw-countdown__sep" aria-hidden="true">:</span>' : ''}` +
            `<span class="otw-countdown__unit" data-unit-wrap="${unit}"><span class="otw-countdown__value" data-unit="${unit}">00</span>` +
            `<span class="otw-countdown__label">${label(unit, fallback)}</span></span>`
        )
        .join('');
      this.querySelectorAll('[data-unit]').forEach((el) => (this.units[el.dataset.unit] = el));
    }

    remaining() {
      if (this.daily) {
        const now = kolkataSecondsOfDay();
        let left = (this.resetAt - now + DAY) % DAY;
        if (left === 0) left = DAY;
        return left;
      }
      if (Number.isNaN(this.end)) return 0;
      return Math.max(0, Math.floor((this.end - Date.now()) / 1000));
    }

    tick() {
      const left = this.remaining();
      if (left !== this.last) {
        if (this.daily && this.last !== null && left > this.last) this.fireExpired();
        this.last = left;
        this.update(left);
        if (!this.daily && left === 0 && !this.expired) {
          this.expired = true;
          this.classList.add('is-expired');
          this.fireExpired();
          return; // stop ticking
        }
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    update(total) {
      const values = {
        days: Math.floor(total / DAY),
        hours: Math.floor((total % DAY) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
      };
      // Without a days slot, fold days into hours
      if (!this.units.days) values.hours += values.days * 24;
      const wrapDays = this.querySelector('[data-unit-wrap="days"]');
      if (wrapDays) {
        const hide = values.days === 0;
        wrapDays.hidden = hide;
        if (wrapDays.nextElementSibling) wrapDays.nextElementSibling.hidden = hide;
      }
      for (const [unit, el] of Object.entries(this.units)) {
        const text = pad(values[unit] ?? 0);
        if (el.textContent !== text) {
          el.textContent = text;
          el.classList.remove('is-tick');
          void el.offsetWidth; // restart the animation
          el.classList.add('is-tick');
        }
      }
      this.setAttribute(
        'aria-label',
        `${values.days ? `${values.days} ${t('days', 'd')} ` : ''}${pad(values.hours)} ${t('hours', 'h')} ${pad(values.minutes)} ${t('minutes', 'm')} ${pad(values.seconds)} ${t('seconds', 's')}`
      );
    }

    fireExpired() {
      this.dispatchEvent(new CustomEvent('expired', { bubbles: true }));
      OTW.emit?.('countdown:expired', { element: this });
    }
  }

  customElements.define('otw-countdown', OtwCountdown);
})();
